import { Component, inject, input, computed } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { HelperCollapse } from "@bitwarden/web-vault/app/shared/utils/helper-collapse";
import { getName } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { GranularityFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/granularity.filter";
import { countPeriods } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { GranularityProperty } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { getDigitCount } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";

interface AccountBalanceDisplay {
  accountID: string;
  account: string;
  balance: number;
}

@Component({
  selector: "app-balance-hud",
  templateUrl: "./balance-hud.component.html",
})
export class BalanceHudComponent {
  private static readonly maxStandardDigits = 11;
  protected readonly helperCollapse = new HelperCollapse();
  private userStoreService = inject(UserStoreService);
  private calculationStoreService = inject(CalculationPastStoreService);
  private granularityFilter = inject(GranularityFilter);
  private readonly isMobile = inject(DeviceDetectorService).isMobile();

  hudIndex = input.required<number>();

  protected readonly periodPrefix = "past";

  private periodData = computed(() => {
    const granularity = this.granularityFilter.selectedGranularity();
    const { startDate, endDate } = this.calculationStoreService.period.selected();
    return {
      count: countPeriods(startDate, endDate, granularity as GranularityProperty),
      granularity,
    };
  });

  protected periodCount = computed(() => this.periodData().count);

  protected periodUnit = computed(() => {
    const { count, granularity } = this.periodData();
    return count === 1 ? granularity : granularity + "s";
  });

  private timeStamp = computed(() => {
    const balances = this.calculationStoreService.balanceByTime.balances();
    if (balances) {
      const keys = [...balances.keys()];
      return keys[this.hudIndex()];
    }
  });

  protected balance = computed(() => {
    const ts = this.timeStamp();
    return ts ? this.calculationStoreService.balanceByTime.balances()?.get(ts) : undefined;
  });

  protected balanceDate = computed(() => {
    const ts = this.timeStamp();
    return ts ? new Date(Number(ts) * 1000) : undefined;
  });

  protected balanceAccounts = computed(() => {
    const balances = this.calculationStoreService.balanceByTimeAndAccounts.balances();
    const ts = this.timeStamp();
    if (!balances || !ts) {
      return undefined;
    }
    const accountBalancesDisplay: AccountBalanceDisplay[] = [];
    balances.get(ts)?.forEach((amount, accountID) => {
      accountBalancesDisplay.push(this.getContext(accountID, amount));
    });
    return accountBalancesDisplay.sort((a, b) => b.balance - a.balance);
  });

  protected hudLabel = computed(() =>
    this.hudIndex() === 0 ? "openingBalance" : "closingBalance",
  );

  protected currency = computed(
    () => this.userStoreService.preferences.preferenceView()?.coreCurrency,
  );

  protected balanceParts = computed(() => this.splitBalance(this.balance()));

  private splitBalance(value: number | undefined) {
    if (value === undefined) {
      return { sign: "", integer: "", decimals: "" };
    }

    const sign = value < 0 ? "-" : "";
    const abs = Math.abs(value);
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(abs);

    // Capture the integer part and the decimal separator + decimals.
    const match = formatted.match(/(.+?)([.,]\d{2})$/);
    if (match) {
      return { sign, integer: match[1], decimals: match[2] };
    }

    return { sign, integer: formatted, decimals: "" };
  }

  private getContext(accountID: string, amount: number): AccountBalanceDisplay {
    return {
      accountID,
      account: getName(this.userStoreService.enrichAccounts.collection(), accountID),
      balance: amount,
    };
  }

  protected useMacroNumber(amount: number | undefined | null): boolean {
    return this.isMobile && getDigitCount(amount) > BalanceHudComponent.maxStandardDigits;
  }
}
